// src/modules/fixture.js — helpers de fixture para la SPA.
//
// FASE 4. Tras el bloque "Prode importado" (21-may-2026) este módulo quedó
// reducido a dos funciones:
//
//   1. `loadFixtureSandbox` — redirige a /fixture-sandbox.html, la página
//      separada multi-page de Vite (herramienta de admin del dueño). Se
//      CONSERVA: el router central la invoca en el hash /#/fixture-sandbox.
//   2. `loadHomeMatches` — alimenta el carrusel "Próximos partidos" del home
//      del grupo (src/modules/grupo.js) con datos reales de Supabase.
//
// El `renderFixture` con las 3 solapas planas que vivía acá fue descartado:
// la pantalla del Mundial dentro de la SPA es ahora src/modules/prode.js
// (importación literal de fixture-sandbox.html).
//
// H1 (26-may-2026, FASE 4): el algoritmo de `loadHomeMatches` se reescribe
// para enriquecer la card del home (estado + fase + ventana ahora-30min →
// fin de mañana) y unificar la persistencia de predicciones con la sección
// Prode. La fuente de verdad de las predicciones es la tabla `predicciones`
// (RPC `guardar_prediccion` vía `core/predicciones.js`).

import { fetchPartidos } from '../core/fixture-db.js'
import { getPredicciones } from '../core/predicciones.js'

// ─── Sandbox externo (conservado) ──────────────────────────────────────────

export const loadFixtureSandbox = () => {
  window.location.href = '/fixture-sandbox.html'
}

// ─── Carrusel "Próximos partidos" del home del grupo ───────────────────────

const bandera = (url) =>
  url
    ? `<img src="${url}" alt="" style="width:34px;height:23px;border-radius:3px;object-fit:cover;vertical-align:middle;">`
    : '⚽'

const ganadorDe = (gl, gv) => (gl > gv ? 'home' : gv > gl ? 'away' : 'draw')

// Decodifica la fila de `predicciones` al pick canónico 'home'|'draw'|'away'
// que consume el carrusel del home (mismo codec que prode.js seedPredictions).
// Para group_match el resultado vive en score_local/score_visitante (1-0, 0-0,
// 0-1). Para playoff_match con empate, `clasificado_codigo` define el pick.
const decodePick = (row, partido) => {
  if (!row) return null
  const sl = row.score_local
  const sv = row.score_visitante
  if (sl > sv) return 'home'
  if (sl < sv) return 'away'
  if (row.prediction_type === 'playoff_match' && row.clasificado_codigo) {
    if (row.clasificado_codigo === partido.equipo_local_codigo) return 'home'
    if (row.clasificado_codigo === partido.equipo_visitante_codigo) return 'away'
  }
  return 'draw'
}

// Adapta una fila de `partidos` al shape que espera el carrusel de grupo.js.
// Exportada en H4 (26-may-2026) para que /dev-sandbox arme mocks de la
// card del home sin tocar Supabase.
export const aPartidoCarrusel = (p, pred = null) => {
  const finalizado = p.status === 'finished'
  const pick = decodePick(pred, p)
  return {
    id: p.partido_id,
    partido_id: p.partido_id,
    fase: p.fase,
    grupo_mundial: p.grupo_mundial,
    fecha_hora: p.fecha_hora,
    equipo_local_codigo: p.equipo_local_codigo,
    equipo_visitante_codigo: p.equipo_visitante_codigo,
    // URLs crudas de bandera al tope: el carrusel de grupos consume `home_team.flag`
    // (img ya armado), pero la card de playoff del home las necesita crudas para
    // reenviarlas a renderPlayoffCardHome (que arma su propio <img>).
    equipo_local_bandera_url: p.equipo_local_bandera_url,
    equipo_visitante_bandera_url: p.equipo_visitante_bandera_url,
    home_team: {
      name: p.equipo_local_nombre || p.equipo_local_codigo || 'A definir',
      code: (p.equipo_local_codigo || '—').slice(0, 3).toUpperCase(),
      flag: bandera(p.equipo_local_bandera_url),
    },
    away_team: {
      name: p.equipo_visitante_nombre || p.equipo_visitante_codigo || 'A definir',
      code: (p.equipo_visitante_codigo || '—').slice(0, 3).toUpperCase(),
      flag: bandera(p.equipo_visitante_bandera_url),
    },
    is_featured: false,
    result_status: p.status,
    // DT-38: goles crudos para mostrar el resultado en vivo centrado en la card
    // del home (mismo dato que usa PRODE en resultOrTime). `result` solo se setea
    // en finished; estos exponen el marcador también mientras está 'live'.
    goles_local: p.goles_local ?? null,
    goles_visitante: p.goles_visitante ?? null,
    // Subestado + minuto del partido en vivo (mismo bloque de estado que PRODE).
    status_detail: p.status_detail ?? null,
    minuto: p.minuto ?? null,
    user_prediction: pick,
    result: finalizado && p.goles_local != null && p.goles_visitante != null
      ? { home: p.goles_local, away: p.goles_visitante, winner: ganadorDe(p.goles_local, p.goles_visitante) }
      : null,
    points_awarded: pred?.puntos_obtenidos ?? null,
    points_status: pred ? 'awarded' : finalizado ? 'no_prediction' : 'pending',
    // H4 migración: la fila cruda de predicción se pasa para que la card
    // playoff del home pueda decodificar (goalsHome/goalsAway/clasificado).
    user_prediction_raw: pred,
  }
}

// DT-38: texto del resultado para el CENTRO de la card del home (entre las dos
// banderas, donde hoy va el "vs"), espejando EXACTO el formato de PRODE
// (resultOrTime → "2 - 0"). Devuelve el marcador cuando el partido está en juego
// o terminado y hay goles; null cuando hay que seguir mostrando el "vs"
// (programado o goles aún null). Pura, testeable. OJO: 0 es un marcador válido
// (0-0), por eso comparamos contra null y no por truthiness.
export const resultadoCentroHome = (match) => {
  const enJuego = match.result_status === 'live' || match.result_status === 'finished'
  if (enJuego && match.goles_local != null && match.goles_visitante != null) {
    return `${match.goles_local} - ${match.goles_visitante}`
  }
  return null
}

// Pure: selecciona partidos a mostrar en el carrusel del home según el
// algoritmo H1 ("día pivote + día siguiente"). Exportada para tests; no
// toca red. La ventana fija "hoy + mañana" se descartó: el Mundial arranca
// el 11-jun y entre rondas hay días sin partidos, dejar el carrusel vacío
// no es lo que queremos.
//
//   1. Particiona: enVivo (status='live'), finalizadosRecientes
//      (status='finished' Y kickoff + 130min está dentro de los últimos 30
//      min), futuros (resto con fecha_hora > ahora).
//   2. Día pivote (TZ local del cliente):
//        - Si hay enVivo → hoy.
//        - Si no hay enVivo pero hay futuros → día del primer futuro.
//        - Si no hay enVivo ni futuros → null.
//   3. Día siguiente: primer día calendario posterior al pivote con ≥1
//      futuro. Puede saltar varios días.
//   4. Candidatos: enVivo + futuros del pivote + futuros del siguiente +
//      finalizadosRecientes.
//   5. Orden: enVivo (ASC) → futuros (ASC) → finalizadosRecientes (DESC).
//   6. Tope: 10 cards.
export const seleccionarPartidosHome = (partidos, ahoraMs = Date.now()) => {
  const conEquipos = (partidos || []).filter(
    (p) => p && p.equipo_local_codigo && p.equipo_visitante_codigo
  )
  if (!conEquipos.length) return []

  const treintaMinMs = 30 * 60 * 1000
  const ciento30MinMs = 130 * 60 * 1000

  // Key de día calendario en TZ local del cliente (getFullYear/Month/Date).
  const dayKey = (ms) => {
    const d = new Date(ms)
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  }

  // Paso 1: buckets
  const enVivo = []
  const enCurso = []
  const futuros = []
  const finalizadosRecientes = []
  for (const p of conEquipos) {
    const fhMs = new Date(p.fecha_hora).getTime()
    if (Number.isNaN(fhMs)) continue
    if (p.status === 'live') {
      enVivo.push(p)
    } else if (p.status === 'finished') {
      const finTeoricoMs = fhMs + ciento30MinMs
      if (ahoraMs >= finTeoricoMs && ahoraMs - finTeoricoMs <= treintaMinMs) {
        finalizadosRecientes.push(p)
      }
    } else if (fhMs > ahoraMs) {
      futuros.push(p)
    } else if (ahoraMs - fhMs <= ciento30MinMs) {
      // DT-37: kickoff YA pasó pero todavía no es 'live' ni 'finished' (el hueco
      // entre el kickoff y que el reloj escriba 'en vivo'). Dentro de la ventana
      // de duración, lo mantenemos visible en el carrusel en vez de dropearlo.
      enCurso.push(p)
    }
  }
  enVivo.sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora))
  futuros.sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora))
  finalizadosRecientes.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora))

  // Paso 2: día pivote
  let diaPivoteKey = null
  if (enVivo.length) {
    diaPivoteKey = dayKey(ahoraMs)
  } else if (futuros.length) {
    diaPivoteKey = dayKey(new Date(futuros[0].fecha_hora).getTime())
  }

  // Paso 3: día siguiente — primer día con futuros distinto al pivote
  let diaSiguienteKey = null
  if (diaPivoteKey) {
    for (const p of futuros) {
      const k = dayKey(new Date(p.fecha_hora).getTime())
      if (k !== diaPivoteKey) {
        diaSiguienteKey = k
        break
      }
    }
  }

  // Paso 4: candidatos futuros (pivote + siguiente)
  const futurosSeleccionados = futuros.filter((p) => {
    const k = dayKey(new Date(p.fecha_hora).getTime())
    return k === diaPivoteKey || k === diaSiguienteKey
  })

  // Pasos 5+6: orden y tope
  return [...enVivo, ...enCurso, ...futurosSeleccionados, ...finalizadosRecientes].slice(0, 10)
}

// Carrusel "Próximos partidos" del home con datos reales + predicción del
// jugador. Sin playerId no carga predicciones (caso defensivo — el home del
// grupo siempre tiene membership, pero la firma queda tolerante).
export const loadHomeMatches = async (playerId = null) => {
  const [partidos, predicciones] = await Promise.all([
    fetchPartidos(),
    playerId ? getPredicciones(playerId) : Promise.resolve([]),
  ])
  const seleccion = seleccionarPartidosHome(partidos)
  const predByPartido = {}
  for (const r of predicciones) predByPartido[r.partido_id] = r
  return seleccion.map((p) => aPartidoCarrusel(p, predByPartido[p.partido_id]))
}
